/**
 * Where a failing check comes from — which lever fixes it. The service sends no such tag, so the
 * app maps it per check from the field it is on and the shape of its id.
 */

/** The four places an issue can come from, in the order the UI lists them. */
export const ISSUE_SOURCES = ['instructions', 'feed', 'attributes', 'rules'] as const;
export type IssueSource = (typeof ISSUE_SOURCES)[number];

/** The five fields the AI writes from its instructions (prompt), in page order. */
export const WRITTEN_FIELDS = [
  'TITLE',
  'DESCRIPTION',
  'PRODUCT_TYPE',
  'PRODUCT_HIGHLIGHT',
  'PRODUCT_DETAIL',
] as const;

/** The written fields as a set — a re-run with new instructions fixes checks on them. */
const INSTRUCTION_FIELDS: ReadonlySet<string> = new Set(WRITTEN_FIELDS);

/** Identifiers and offer data the merchant's feed supplies as-is; only a feed change fixes them. */
const FEED_FIELDS: ReadonlySet<string> = new Set([
  'GTIN',
  'MPN',
  'BRAND',
  'IDENTIFIER_EXISTS',
  'ITEM_GROUP_ID',
  'CATEGORY',
  'GOOGLE_PRODUCT_CATEGORY',
  'SHORT_TITLE',
  'SIZE',
  'SIZE_TYPE',
  'SIZE_SYSTEM',
  'PRICE',
  'SALE_PRICE',
  'AVAILABILITY',
  'CONDITION',
  'IS_BUNDLE',
  'MULTIPACK',
  'IMAGE_LINK',
  'ADDITIONAL_IMAGE_LINK',
  'PRODUCT_LENGTH',
  'PRODUCT_WIDTH',
  'PRODUCT_HEIGHT',
  'PRODUCT_WEIGHT',
  'SHIPPING_LENGTH',
  'SHIPPING_WIDTH',
  'SHIPPING_HEIGHT',
  'SHIPPING_WEIGHT',
]);

/** Descriptive attributes the service extracts (or the feed carries) — fixed by re-extraction. */
const ATTRIBUTE_FIELDS: ReadonlySet<string> = new Set([
  'COLOR',
  'MATERIAL',
  'PATTERN',
  'GENDER',
  'AGE_GROUP',
]);

/**
 * Check-id endings that compare one field with another (`gender.agrees_with_title`,
 * `brand.identified`, `structured_title.mirrors_title`, `shipping.not_copied` …) — GMC v2's
 * `agrees_with`, `equals_field`, `any_present`, `all_or_none`, `fields_differ`,
 * `fields_same_unit` kinds and the AI-disclosure checks.
 */
const CROSS_FIELD_CHECK =
  /\.(agrees_with_\w+|mirrors_\w+|identified|all_or_none|same_unit|not_copied|ai_disclosure)$/;

/** Product facts that can move a check's source — what the app knows about this product's feed. */
export interface IssueSourceContext {
  /** False when the feed carries no dimensions (length / width / height / size of the item). */
  feedHasDimensions?: boolean;
}

/**
 * Documented overrides: checks on a written field that the AI can only pass when the feed holds
 * the fact. When the feed lacks it, a rewrite cannot fix it, so the issue belongs to the feed.
 * Keep this list short — every entry is a judgement about one check id.
 */
const FEED_DEPENDENT_CHECKS: Record<string, keyof IssueSourceContext> = {
  // "Description states the dimensions" — the AI must not invent sizes the feed doesn't give.
  'description.states_dimensions': 'feedHasDimensions',
};

/**
 * Where an issue comes from. In order:
 * 0. A feed-dependent check (see `FEED_DEPENDENT_CHECKS`) whose fact the feed lacks → `feed`.
 * 1. Any check on one of the five written fields → `instructions` (a rewrite fixes it, even a
 *    cross-field one like `title.has_brand`).
 * 2. A cross-field check (by id ending, or an id prefix that is not the field it runs on, e.g.
 *    `shipping.all_or_none` on SHIPPING_LENGTH), or one on the structured title/description
 *    → `rules` (a relationship between fields or the rules' own structure, not one value).
 * 3. Feed identifiers and offer data → `feed`; descriptive attributes → `attributes`.
 * 4. Anything unrecognised → `rules`, so nothing is wrongly blamed on the feed or the prompt.
 */
export function issueSource(
  checkId: string,
  field: string,
  context: IssueSourceContext = {},
): IssueSource {
  const needs = FEED_DEPENDENT_CHECKS[checkId];
  if (needs !== undefined && context[needs] === false) return 'feed';
  const code = field.toUpperCase();
  if (INSTRUCTION_FIELDS.has(code)) return 'instructions';
  const dot = checkId.indexOf('.');
  const prefix = dot >= 0 ? checkId.slice(0, dot).toUpperCase() : '';
  const crossField = CROSS_FIELD_CHECK.test(checkId) || (prefix !== '' && prefix !== code);
  if (crossField || code.startsWith('STRUCTURED_')) return 'rules';
  if (FEED_FIELDS.has(code)) return 'feed';
  if (ATTRIBUTE_FIELDS.has(code)) return 'attributes';
  return 'rules';
}

/** Plain-language label for each source, as the issue lists show it. */
export const ISSUE_SOURCE_LABELS: Record<IssueSource, string> = {
  instructions: 'Instructions',
  feed: 'Feed',
  attributes: 'Attributes',
  rules: 'Rules',
};
